import { useState, useEffect, useRef, useCallback } from 'react'
import JSZip from 'jszip'
import { useAuth } from '../auth/AuthContext.jsx'
import { getLines as getClientLines } from '../utils/consoleCapture.js'
import api from '../api/client'

const LEVELS = ['DEBUG', 'INFO', 'WARNING', 'ERROR']

const LEVEL_STYLE = {
  DEBUG:    { color: '#475569' },
  INFO:     { color: '#94a3b8' },
  WARNING:  { color: '#f59e0b' },
  ERROR:    { color: '#f87171' },
  CRITICAL: { color: '#e879f9' },
}

const POLL_MS = 3000

// ── Debug bundle download ─────────────────────────────────────────────────────

async function downloadDebugBundle(serverLines) {
  const ts   = new Date().toISOString().replace(/[:.]/g, '-')
  const meta = [
    `AEO Debug Bundle`,
    `Generated:  ${new Date().toISOString()}`,
    `User-Agent: ${navigator.userAgent}`,
    `URL:        ${window.location.href}`,
  ].join('\n')

  const serverText  = serverLines.map(l =>
    `${l.ts}  ${l.level.padEnd(8)} ${l.logger.padEnd(40)} ${l.message}`
  ).join('\n')

  const clientLines = getClientLines()
  const clientText  = clientLines.map(l =>
    `${l.ts}  ${l.level.padEnd(8)} ${l.message}`
  ).join('\n')

  const zip = new JSZip()
  zip.file('bundle-info.txt',   meta)
  zip.file('server.log',        serverText  || '(no server log lines)')
  zip.file('client-console.log', clientText || '(no client console lines)')

  const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href     = url
  a.download = `aeo-debug-bundle-${ts}.zip`
  a.click()
  setTimeout(() => URL.revokeObjectURL(url), 5000)
}

// ── Notify Support modal ──────────────────────────────────────────────────────

function NotifySupportModal({ serverLines, onClose }) {
  const [subject, setSubject]   = useState('AEO Support Request')
  const [message, setMessage]   = useState('')
  const [attach, setAttach]     = useState(true)
  const [sending, setSending]   = useState(false)
  const [result, setResult]     = useState(null)   // null | 'sent' | {error}

  const handleSend = async () => {
    if (!message.trim()) return
    setSending(true)
    try {
      const recentErrors = attach
        ? serverLines.filter(l => l.level === 'ERROR' || l.level === 'WARNING').slice(-30)
        : null
      await api.post('/admin/notify-support', { subject, message, recent_errors: recentErrors })
      setResult('sent')
    } catch (e) {
      setResult({ error: e?.response?.data?.detail ?? e.message })
    } finally {
      setSending(false)
    }
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{
        width: 480, borderRadius: 14, padding: '24px 28px',
        background: 'rgba(10,15,30,0.98)', border: '1px solid rgba(255,255,255,0.1)',
        boxShadow: '0 20px 60px rgba(0,0,0,0.7)',
      }}>
        {result === 'sent' ? (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>✅</div>
            <p style={{ color: '#34d399', fontWeight: 600, margin: '0 0 8px' }}>Email sent to support@cerebi.ai</p>
            <p style={{ color: '#64748b', fontSize: 12, margin: '0 0 20px' }}>The team will reply to your email address.</p>
            <button onClick={onClose} style={btnStyle('#475569')}>Close</button>
          </div>
        ) : (
          <>
            <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: '#e2e8f0' }}>
              Notify Support
            </h3>

            <label style={labelStyle}>Subject</label>
            <input
              value={subject}
              onChange={e => setSubject(e.target.value)}
              style={inputStyle}
            />

            <label style={labelStyle}>Message</label>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Describe the issue…"
              rows={5}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
            />

            <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={attach}
                onChange={e => setAttach(e.target.checked)}
                style={{ accentColor: '#00d4ff' }}
              />
              Include recent errors &amp; warnings in email body
            </label>

            {result?.error && (
              <div style={{
                margin: '12px 0 0', padding: '8px 12px', borderRadius: 8, fontSize: 12,
                background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171',
              }}>
                {result.error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={btnStyle('#334155')}>Cancel</button>
              <button
                onClick={handleSend}
                disabled={sending || !message.trim()}
                style={btnStyle('#00d4ff', !message.trim() || sending)}
              >
                {sending ? 'Sending…' : 'Send Email'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

const labelStyle = { display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4, marginTop: 12 }
const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 7,
  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
  color: '#e2e8f0', fontSize: 13, outline: 'none',
}
const btnStyle = (accent, disabled = false) => ({
  padding: '8px 18px', borderRadius: 7, cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: 12, fontWeight: 600, transition: 'opacity 0.15s',
  background: `${accent}18`, border: `1px solid ${accent}60`,
  color: accent, opacity: disabled ? 0.45 : 1,
})

// ── Main page ─────────────────────────────────────────────────────────────────

export default function Logs() {
  const { user } = useAuth()
  const [lines, setLines]           = useState([])
  const [minLevel, setMinLevel]     = useState('INFO')
  const [follow, setFollow]         = useState(true)
  const [error, setError]           = useState(null)
  const [bundling, setBundling]     = useState(false)
  const [showSupport, setShowSupport] = useState(false)
  const bottomRef                   = useRef(null)
  const pollRef                     = useRef(null)

  const isCerebi = user?.email?.endsWith('@cerebi.ai')

  const fetchLogs = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/logs', { params: { level: minLevel, limit: 500 } })
      setLines(data.lines)
      setError(null)
    } catch (e) {
      setError(e?.response?.data?.detail ?? e.message)
    }
  }, [minLevel])

  useEffect(() => {
    if (!isCerebi) return
    fetchLogs()
    pollRef.current = setInterval(fetchLogs, POLL_MS)
    return () => clearInterval(pollRef.current)
  }, [isCerebi, fetchLogs])

  useEffect(() => {
    if (follow) bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [lines, follow])

  const handleBundle = async () => {
    setBundling(true)
    try {
      const { data } = await api.get('/admin/logs', { params: { level: 'DEBUG', limit: 1000 } })
      await downloadDebugBundle(data.lines)
    } catch (e) {
      setError('Bundle generation failed: ' + (e.message ?? String(e)))
    } finally {
      setBundling(false)
    }
  }

  if (!isCerebi) {
    return (
      <div className="max-w-7xl mx-auto px-4 pt-8 text-center">
        <p style={{ color: '#f87171', fontSize: 14 }}>Access restricted to cerebi.ai accounts.</p>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 pt-6 pb-4 flex flex-col" style={{ height: 'calc(100vh - 4rem)' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3 flex-shrink-0 flex-wrap gap-2">
        <h1 style={{ fontSize: 18, fontWeight: 700, color: '#e2e8f0', margin: 0 }}>Server Logs</h1>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Level filter */}
          <div className="flex items-center gap-1">
            {LEVELS.map(l => (
              <button key={l} onClick={() => setMinLevel(l)} style={{
                padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                cursor: 'pointer', transition: 'all 0.15s',
                background: minLevel === l ? 'rgba(0,212,255,0.15)' : 'rgba(255,255,255,0.04)',
                border: minLevel === l ? '1px solid rgba(0,212,255,0.4)' : '1px solid rgba(255,255,255,0.08)',
                color: minLevel === l ? '#00d4ff' : '#64748b',
              }}>
                {l}
              </button>
            ))}
          </div>

          {/* Follow toggle */}
          <button onClick={() => setFollow(f => !f)} style={{
            padding: '3px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600,
            cursor: 'pointer', transition: 'all 0.15s',
            background: follow ? 'rgba(16,185,129,0.12)' : 'rgba(255,255,255,0.04)',
            border: follow ? '1px solid rgba(16,185,129,0.35)' : '1px solid rgba(255,255,255,0.08)',
            color: follow ? '#34d399' : '#64748b',
          }}>
            {follow ? '⬇ Follow' : 'Follow'}
          </button>

          <span style={{ fontSize: 11, color: '#334155' }}>|</span>

          {/* Debug Bundle */}
          <button onClick={handleBundle} disabled={bundling} style={{
            padding: '4px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600,
            cursor: bundling ? 'not-allowed' : 'pointer', transition: 'all 0.15s', opacity: bundling ? 0.6 : 1,
            background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.35)', color: '#a78bfa',
          }}>
            {bundling ? 'Bundling…' : '⬇ Debug Bundle'}
          </button>

          {/* Notify Support */}
          <button onClick={() => setShowSupport(true)} style={{
            padding: '4px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600,
            cursor: 'pointer', transition: 'all 0.15s',
            background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.3)', color: '#00d4ff',
          }}>
            ✉ Notify Support
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          marginBottom: 8, padding: '8px 12px', borderRadius: 8, fontSize: 12,
          background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171',
        }}>
          {error}
        </div>
      )}

      {/* Log terminal */}
      <div
        style={{
          flex: 1, minHeight: 0, overflowY: 'auto', borderRadius: 10,
          background: 'rgba(5,8,20,0.95)', border: '1px solid rgba(255,255,255,0.07)',
          padding: '10px 14px', fontFamily: 'ui-monospace, monospace', fontSize: 12,
        }}
        onScroll={e => {
          const el = e.currentTarget
          setFollow(el.scrollHeight - el.scrollTop - el.clientHeight < 40)
        }}
      >
        {lines.length === 0 && <span style={{ color: '#334155' }}>No log lines yet…</span>}
        {lines.map((line, i) => (
          <div key={i} style={{ display: 'flex', gap: 10, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            <span style={{ color: '#334155', flexShrink: 0 }}>{line.ts}</span>
            <span style={{ ...(LEVEL_STYLE[line.level] ?? { color: '#94a3b8' }), flexShrink: 0, minWidth: 52 }}>
              {line.level}
            </span>
            <span style={{ color: '#334155', flexShrink: 0, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {line.logger}
            </span>
            <span style={{ color: '#cbd5e1', flex: 1 }}>{line.message}</span>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {showSupport && (
        <NotifySupportModal serverLines={lines} onClose={() => setShowSupport(false)} />
      )}
    </div>
  )
}
